import { useEffect, useRef, useState, type ComponentType } from 'react'
import { fetchMsgHtml, type MsgDeskPhpModelResponse } from '@/msgdeskRuntime/transport/msgModelClient'
import { INITIAL_PHP_MODEL_PREFIX } from '@/msgdeskRuntime/legacyBridge/legacyRequestParams'
import { SubsManager } from '@/state/MsgTreeProvider'
import { treeStore, type BootCollectionSpan, type SubsStoreState } from '@/msgdeskRuntime/state'
import { decodePayloadJson } from '@/msgdeskRuntime/state/payloadJson'
import { markLegacyCommandLive } from '@/msgdeskRuntime/legacyBridge/preBootQueue'
import { contractSelector, installLegacySelectorAliasesGlobal, legacyClass, legacyCssClass, legacyId, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { getMsgDeskRuntime, type MsgDeskParams } from '@/msgdeskRuntime'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import {
  closedEditorSession,
  EditorSessionProvider,
  type EditorSessionIdentity,
} from './deskUiSkeletons/editor/EditorSessionContext'
import { DeskDocumentTitle } from './deskUiSkeletons/Shared/DeskDocumentTitle'

// Parallel (v2) app. Same data ownership + boot sequence as LegacyReactApp,
// but renders the hand-componented <MsgPage/> instead of parsing raw HTML.
// Legacy still owns all data; React is the visual layer. No existing file is
// modified — importing treeStore installs the same global bridges.

const runtime = getMsgDeskRuntime('template2')
const w = new Proxy({} as any, {
  get(_target, key) {
    return (runtime.legacyWindow as any)?.[key as keyof Window]
  },
  set(_target, key, value) {
    const legacyWindow = runtime.legacyWindow as any
    if (legacyWindow) legacyWindow[key as keyof Window] = value
    return true
  },
  deleteProperty(_target, key) {
    const legacyWindow = runtime.legacyWindow as any
    if (legacyWindow) delete legacyWindow[key as keyof Window]
    return true
  },
  has(_target, key) {
    return Boolean(runtime.legacyWindow && key in (runtime.legacyWindow as any))
  },
})

function cacheLegacyElsNames(elsNames: unknown): unknown {
  if (!elsNames || typeof elsNames !== 'object') return w.__MSG_V2_ELS_NAMES__ || null
  w.__MSG_V2_ELS_NAMES__ = elsNames
  return elsNames
}

function cachedLegacyElsNames(): unknown {
  return w.__MSG_V2_ELS_NAMES__ || null
}

function hasMsgInputs(dataObj: any, elId?: number): boolean {
  return !!dataObj?.msg_inputs?.(elId || 0)
}

function legacyTruthy(value: unknown): boolean {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true' || normalized === 'yes'
  }
  return false
}

function isForbiddenByParentValues(dataObj: any, isUpdate: boolean, isMsg: boolean): boolean {
  if (isUpdate) return false
  const forbidSubs = dataObj?.is_values?.('is_forbid_subs')
  if (!Array.isArray(forbidSubs)) return false
  return legacyTruthy(forbidSubs[isMsg ? 1 : 0])
}

function canOpenEditorLocally(
  dataObj: any,
  isUpdate: boolean,
  isMsg: boolean,
  isEditPin: boolean,
  isEditLocked: boolean,
  elId?: number,
): boolean {
  return hasMsgInputs(dataObj, elId)
    && (!isEditPin || !isEditLocked)
    && !isForbiddenByParentValues(dataObj, isUpdate, isMsg)
}

function syncLegacyRuntimeAfterEditorInit(reason: string) {
  treeStore.mutate.syncLegacyRuntime?.(reason)
  // Republish the rough as the React editor model too: show_form may have just
  // filled the previously default-empty rough, and a stale empty snapshot must
  // not stay latched in templateData.editorFormModel. No-ops until the editor
  // contracts exist.
  dispatchEditorAction({ action: 'sync-model', reason: `${reason}:rough` })
}

function applyNewFormResponseMetadata(
  parsed: Record<string, any>,
  context: {
    pin: string
    requestPin: string | false
    parentData: Record<string, unknown> | null
    dataObj: any
    elsObj: any
    isEditPin: boolean
    isEditLocked: boolean
    elId?: number
  },
): string {
  const responsePin = normalizeLegacyPin(parsed.show_pin) || context.pin
  parsed.show_pin = responsePin || false

  if (context.isEditPin) {
    if (parsed.subs_count) {
      context.dataObj.subs_count?.('trash', false, parsed.subs_count.trash?.[0])
      context.dataObj.subs_count?.('trash', true, parsed.subs_count.trash?.[1])
      context.dataObj.subs_count?.('drafts', false, parsed.subs_count.drafts?.[0])
      context.dataObj.subs_count?.('drafts', true, parsed.subs_count.drafts?.[1])
      context.dataObj.subs_count?.('favs', false, parsed.subs_count.favs?.[0])
      context.dataObj.subs_count?.('favs', true, parsed.subs_count.favs?.[1])
    }

    const editRuntimePin = context.requestPin === '0' ? '' : context.requestPin
    context.dataObj.is_not_locked?.(context.elId, true, editRuntimePin)
    context.dataObj.is_not_locked?.(context.elId, false, parsed.show_pin)

    const formVals = context.dataObj.form_vals?.('protect')
    if (formVals && !formVals[2] && !formVals[1]?.[2]) formVals[1][1] = editRuntimePin

    if (context.isEditLocked) context.elsObj.un_edit_lock?.()
  }

  if (context.parentData && typeof context.parentData === 'object') {
    context.parentData.upload_settings = parsed.upload_settings
    if (responsePin) {
      context.parentData.show_pin = responsePin
      context.parentData.edit_pin = responsePin
      if ('showpin' in context.parentData) context.parentData.showpin = responsePin
      if ('editpin' in context.parentData) context.parentData.editpin = responsePin
    }
  }

  return responsePin
}

function ensureBodyV2Classes() {
  runtime.body?.classList.add('md-page-style')
}

function elementsFromLegacyTarget(target: any): HTMLElement[] {
  if (!target) return []
  if (target instanceof HTMLElement) return [target]
  if (typeof target === 'string') {
    if (target === '#mcss-bodyID-cssm' || target === 'body') return runtime.body ? [runtime.body] : []
    try {
      return runtime.querySelectorAll(target).filter((el): el is HTMLElement => el instanceof HTMLElement)
    } catch {
      return []
    }
  }
  if (target[0] instanceof HTMLElement) return Array.from(target as ArrayLike<HTMLElement>).filter((el): el is HTMLElement => el instanceof HTMLElement)
  if (target.el instanceof HTMLElement) return [target.el]
  return []
}

function applyKnownV2VisualClass(target: any, className: unknown, mode: 'add' | 'remove' | 'toggle', state?: boolean): boolean {
  const cls = String(className || '')
  if (!cls) return false
  const elements = elementsFromLegacyTarget(target)
  if (!elements.length) return false

  const enabled = mode === 'toggle' ? state : mode === 'add'
  let handled = false
  for (const element of elements) {
    if (cls === 'mcss-page_style_div-cssm') {
      element.classList.toggle('md-page-style', enabled ?? !element.classList.contains('md-page-style'))
      handled = true
    } else if (cls === 'mcss-display_none-cssm') {
      element.classList.toggle('hidden', enabled ?? !element.classList.contains('hidden'))
      handled = true
    } else if (/^mcss-mode_[A-Za-z0-9_-]+-cssm$/.test(cls)) {
      handled = true
    } else if (cls === 'mcss-menu_over-cssm') {
      element.classList.toggle('md-sidebar__nav-item--hover', enabled ?? !element.classList.contains('md-sidebar__nav-item--hover'))
      handled = true
    } else if (cls === 'mcss-menu_selected-cssm') {
      element.classList.toggle('md-sidebar__nav-item--selected', enabled ?? !element.classList.contains('md-sidebar__nav-item--selected'))
      handled = true
    }
  }
  return handled
}

function splitLegacyClassTokens(className: unknown) {
  return String(className || '').split(/\s+/).filter(Boolean)
}

function consumeKnownV2VisualClasses(target: any, className: unknown, mode: 'add' | 'remove' | 'toggle', state?: boolean) {
  const remaining: string[] = []
  let handled = false
  for (const token of splitLegacyClassTokens(className)) {
    if (applyKnownV2VisualClass(target, token, mode, state)) handled = true
    else remaining.push(token)
  }
  return { handled, remaining: remaining.join(' ') }
}

function installLegacyVisualClassFacade() {
  const allEls = w.all_els_obj
  if (!allEls || allEls.__MSG_V2_VISUAL_CLASS_FACADE__) return

  const originalAddClass = allEls.addClass?.bind(allEls)
  const originalRemoveClass = allEls.removeClass?.bind(allEls)
  const originalToggleClass = allEls.toggleClass?.bind(allEls)

  allEls.addClass = function addClassFacade(target: any, className: unknown, ...rest: unknown[]) {
    const result = consumeKnownV2VisualClasses(target, className, 'add')
    if (!result.remaining) return target
    return originalAddClass?.(target, result.remaining, ...rest)
  }
  allEls.removeClass = function removeClassFacade(target: any, className: unknown, ...rest: unknown[]) {
    const result = consumeKnownV2VisualClasses(target, className, 'remove')
    if (!result.remaining) return target
    return originalRemoveClass?.(target, result.remaining, ...rest)
  }
  allEls.toggleClass = function toggleClassFacade(target: any, className: unknown, state?: boolean, ...rest: unknown[]) {
    const result = consumeKnownV2VisualClasses(target, className, 'toggle', state)
    if (!result.remaining) return target
    return originalToggleClass?.(target, result.remaining, state, ...rest)
  }

  allEls.__MSG_V2_VISUAL_CLASS_FACADE__ = true
}

// Capture legacy's exact loading count into the store. Legacy hands it to
// els.wait_el(mode, count, $before_el, el_id): el_id 0 = top-level tab load,
// el_id > 0 = a sub's load/open. Empty mode = load finished.
function installLegacySubsLoadingBridge() {
  const els = w.MSG_OBJ?.els_obj || w.msgdsk?.obj?.('els')
  if (!els || typeof els.wait_el !== 'function' || els.__MSG_V2_SUBS_LOADING_BRIDGE__) return
  const original = els.wait_el
  els.wait_el = function (...args: any[]) {
    const mode = args[0]
    treeStore.mutate.setSubsLoading?.(mode
      ? { mode: String(mode), count: Number(args[1]) || 0, elId: Number(args[3]) || 0 }
      : null)
    return original.apply(els, args)
  }
  els.__MSG_V2_SUBS_LOADING_BRIDGE__ = true
}

function isLegacyEditorMode(mode: unknown): boolean {
  return mode === 'update_msg' || mode === 'new_msg' || mode === 'new_submsg' || mode === 'new_answer'
}

function currentLegacyMode(): EditorMode {
  const modeFn = w.msgdsk?.obj?.('modes')?.dsk_mode || w.dsk?.obj?.('modes')?.dsk_mode || w.dsk_mode
  return typeof modeFn === 'function' ? String(modeFn() || '') : ''
}

function workspaceContractForMode(mode: string): LegacyContractName {
  if (isLegacyEditorMode(mode)) return 'workspace.editor'
  if (mode === 'favs') return 'workspace.favs'
  if (mode === 'trash') return 'workspace.trash'
  if (mode === 'drafts') return 'workspace.drafts'
  if (mode === 'attachments') return 'workspace.attachments'
  return 'workspace.main'
}

function forceWorkspaceVisibility(mode: string) {
  const activeContract = workspaceContractForMode(mode)
  const mainVisible = activeContract === 'workspace.main'
  const editorVisible = activeContract === 'workspace.editor'
  const contracts: LegacyContractName[] = [
    'workspace.main',
    'workspace.editor',
    'workspace.attachments',
    'workspace.favs',
    'workspace.trash',
    'workspace.drafts',
    'main.root',
    'subs.toolbar',
    'subs.answers.front',
    'subs.submsges.front',
    'editor.root',
  ]

  for (const contract of contracts) {
    const visible = contract.startsWith('workspace.')
      ? contract === activeContract
      : contract === 'editor.root'
        ? editorVisible
        : contract === 'main.root' || contract === 'subs.toolbar'
          ? mode === 'msg'
          : contract === 'subs.answers.front'
            ? mainVisible && mode !== 'submsges'
            : contract === 'subs.submsges.front'
              ? mainVisible && mode !== 'answers'
              : mainVisible
    treeStore.mutate.setContractVisible?.(contract, visible)
    const el = runtime.querySelector(contractSelector(contract))
    if (el instanceof HTMLElement) {
      el.toggleAttribute('data-md-react-display', !visible)
      if (visible && runtime.browserWindow?.getComputedStyle(el).display === 'none') {
        el.style.display = ''
      }
    }
  }
  treeStore.mutate.projectChromeState?.()
}

// S1: forceWorkspaceVisibility calls are routed through this guard. While the
// initial-route projection hold is active (SSR folder/tab route, legacy still
// on its boot 'msg' mode), the transient main-mode stomp is skipped so the
// server-rendered chrome persists; any other mode releases the hold.
function applyWorkspaceVisibility(mode: string) {
  const hold = treeStore.mutate.getInitialProjectionHold?.()
  if (hold) {
    if (mode === 'msg' || mode === 'subs') return
    treeStore.mutate.setInitialProjectionHold?.(null)
  }
  forceWorkspaceVisibility(mode)
}

function installLegacyModeVisibilityBridge(syncEditorOpen: (isOpen: boolean, mode: EditorMode) => void) {
  const els = w.MSG_OBJ?.els_obj || w.msgdsk?.obj?.('els')
  if (!els || typeof els.show_mode !== 'function' || els.__MSG_V2_MODE_VISIBILITY_BRIDGE__) return false

  const originalShowMode = els.show_mode.bind(els)
  els.show_mode = function v2ModeVisibilityBridge(...args: any[]) {
    try {
      return originalShowMode(...args)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (!message.includes('last_el_id')) throw error
      return undefined
    } finally {
      const mode = currentLegacyMode()
      applyWorkspaceVisibility(mode)
      syncEditorOpen(isLegacyEditorMode(mode), mode)
    }
  }

  els.__MSG_V2_MODE_VISIBILITY_BRIDGE__ = true
  const mode = currentLegacyMode()
  applyWorkspaceVisibility(mode)
  syncEditorOpen(isLegacyEditorMode(mode), mode)
  return true
}

function activeEditorSession(
  current: EditorSessionIdentity,
  mode: EditorMode,
  explicitIsMsg?: boolean,
): EditorSessionIdentity {
  const isMsg = typeof explicitIsMsg === 'boolean'
    ? explicitIsMsg
    : mode === 'new_answer'
      ? false
      : mode === 'new_msg' || mode === 'new_submsg'
        ? true
        : current.mode === mode
          ? current.isMsg
          : undefined
  return { mode, isMsg }
}

function legacyAlertMessage(msgType: unknown, infoData: unknown) {
  if (typeof infoData === 'string' && infoData.trim()) return infoData
  if (msgType === 'locked_msg' || msgType === 'locked_msg_2') return 'Please, enter edit pin'
  return ''
}

type ShowPinDialog = (request: {
  message?: string
  okText?: string
  cancelText?: string
  onClose: (pin?: string) => void
}) => void

function installLegacyPinAlertFacade(showPinDialog: ShowPinDialog) {
  const alertObj = w.MSG_OBJ?.alert_obj || w.msgdsk?.obj?.('alert') || w.dsk?.obj?.('alert')
  if (!alertObj || alertObj.__MSG_V2_PIN_ALERT_FACADE__) return false

  const originalAlert = typeof alertObj.alert === 'function' ? alertObj.alert.bind(alertObj) : null
  const originalModalAlert = typeof alertObj.modal_alert === 'function' ? alertObj.modal_alert.bind(alertObj) : null

  const routePinAlert = (original: ((...args: any[]) => unknown) | null, args: any[]) => {
    const [msgType, infoData, callback] = args
    if (msgType === 'locked_msg' || msgType === 'locked_msg_2') {
      showPinDialog({
        message: legacyAlertMessage(msgType, infoData),
        onClose(pin?: string) {
          if (typeof callback === 'function') callback(pin)
        },
      })
      return undefined
    }
    return original?.(...args)
  }

  if (originalAlert) {
    alertObj.alert = function v2PinAlertFacade(...args: any[]) {
      return routePinAlert(originalAlert, args)
    }
  }

  if (originalModalAlert) {
    alertObj.modal_alert = function v2PinModalAlertFacade(...args: any[]) {
      return routePinAlert(originalModalAlert, args)
    }
  }

  alertObj.__MSG_V2_PIN_ALERT_FACADE__ = true
  return true
}

function installLegacyBusyNoop() {
  const utils = w.MSG_UTILS || w.msgdsk?.obj?.('utils') || w.dsk?.obj?.('utils')
  if (!utils || typeof utils.is_busy !== 'function' || utils.__MSG_V2_BUSY_NOOP__) return false

  const noopBusy = function v2BusyNoop(...args: unknown[]) {
    void args
    return false
  }

  utils.is_busy = noopBusy
  if (w.MSG_UTILS && typeof w.MSG_UTILS === 'object') w.MSG_UTILS.is_busy = noopBusy
  utils.__MSG_V2_BUSY_NOOP__ = true

  runtime.facade.appMsgObj?.legacy?.noteBusyTransition?.('busy:global-release', 'v2-busy-noop')

  return true
}

function rebindLegacyNewForm() {
  const n = w.msgdsk?.mod?.('new_obj')
  if (!n) return
  n.mod?.('dom_els')?.refresh?.()
  n.mod?.('page_els')?.rebind_live_dom?.()
}

function rebindLegacyNewFormSaverAtts() {
  const n = w.msgdsk?.mod?.('new_obj')
  if (!n) return
  n.mod?.('dom_els')?.refresh?.()
  n.mod?.('page_els')?.rebind_live_dom?.()
  n.mod?.('saver')?.attaches?.rebind_live_dom?.()
}

// Option 3: legacy's check_form runs the jQuery-validate plugin whose validator
// is never created on the React-owned form (we keep legacy off the form), so it
// throws on Save. v2 does not validate client-side — the server re-validates on
// the save request — so stub check_form to report "ok" and let the saver proceed.
// `all_errs` is empty (no validator ran), so "ok" is exactly what the real
// function would return if it didn't throw.
function installLegacyCheckFormStub() {
  const checkers = w.msgdsk?.mod?.('new_obj')?.mod?.('checkers')
  if (!checkers || checkers.__v2CheckFormStubbed) return
  checkers.check_form = function () { return 'ok' }
  checkers.__v2CheckFormStubbed = true
}

function dispatchLegacyInlineClick(el: HTMLElement, inlineScript: string): boolean {
  const newMsgObj = runtime.facade.legacyNewMsgObj
  const msgObj = runtime.facade.legacyMsgObj
  const attToggle = inlineScript.match(/NEW_MSG_OBJ\.att_toggle\s*\(\s*this\s*,\s*(true|false)\s*,\s*(\d+)\s*\)/)
  if (attToggle) {
    newMsgObj?.att_toggle?.(el, attToggle[1] === 'true', Number(attToggle[2]))
    return true
  }

  if (/NEW_MSG_OBJ\.to_address_remove\s*\(\s*this\s*\)/.test(inlineScript)) {
    newMsgObj?.to_address_remove?.(el)
    return true
  }

  const toggleImgMatch = inlineScript.match(/MSG_OBJ\.(?:toggle_img|mev_msg_toggle_img_evm)\s*\(\s*this\s*(?:,\s*(true|false))?\s*\)/)
  if (toggleImgMatch) {
    const toggleImg = msgObj?.toggle_img || msgObj?.mev_msg_toggle_img_evm
    const isNext = toggleImgMatch[1] === undefined ? undefined : toggleImgMatch[1] === 'true'
    toggleImg?.call(msgObj, el, isNext)
    return true
  }

  if (/\$\(\s*["']input\[name=attach_file\]["']\s*\)\.click\s*\(\s*\)/.test(inlineScript)) {
    runtime.querySelector<HTMLInputElement>('input[name=attach_file]')?.click()
    return true
  }

  return false
}

function installLegacyInlineClickBridge() {
  const handleLegacyInlineClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) return
    const el = event.target.closest<HTMLElement>('[onclick]')
    const inlineScript = el?.getAttribute('onclick') || ''
    if (!el || !inlineScript) return

    try {
      if (!dispatchLegacyInlineClick(el, inlineScript)) return
    } catch (error) {
      console.warn('[v2 legacy inline click]', error)
    }

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  runtime.addDocumentEventListener('click', handleLegacyInlineClick, true)
  return () => runtime.removeDocumentEventListener('click', handleLegacyInlineClick, true)
}

function editorHolder(): HTMLElement | null {
  const workspaceEditorClass = legacyClass('workspace.editor')
  const holder = runtime.querySelector(contractSelector('workspace.editor')) || (workspaceEditorClass ? runtime.querySelector(`.${workspaceEditorClass}`) : null)
  return holder instanceof HTMLElement ? holder : null
}

function isReactEditorRendered(): boolean {
  return !!runtime.querySelector(contractSelector('editor.root'))
}

// The PHP flow injected the form HTML synchronously BEFORE new_obj.show_form,
// so legacy element handles, protect-pin state writes, and the value fill
// (show_form_2 -> set_form) always found the DOM. renderEditorTemplate() is an
// async React commit — running show_form in the same task points legacy at a
// not-yet-rendered editor (signed-in local open: the fields stayed empty and a
// premature empty rough snapshot latched into the React editor model). Restore
// the DOM-before-show_form ordering by deferring until the contract exists.
function whenEditorTemplateRendered(run: () => void) {
  if (isReactEditorRendered()) {
    run()
    return
  }
  let attempts = 0
  const tick = () => {
    if (isReactEditorRendered() || attempts >= 120) {
      run()
      return
    }
    attempts += 1
    setTimeout(tick, 16)
  }
  setTimeout(tick, 0)
}

function fatalEditorFragmentFallback(action: string, selector: string) {
  const detail = { action, selector }
  runtime.recordFallback({ op: 'editorFragment', ...detail })
  throw new Error(`[v2 strict] Unvirtualized editor fragment: ${JSON.stringify(detail)}`)
}

function replaceEditorNode(selector: string, html: string): boolean {
  const ok = treeStore.mutate.replaceEditorFormNodeHtml?.(selector, html)
  if (!ok) {
    fatalEditorFragmentFallback('replace', selector)
    return false
  }
  return true
}

function replaceOrInsertEditorNode(selector: string, insertAfterSelector: string, html: string): boolean {
  const ok = treeStore.mutate.replaceOrInsertEditorFormNodeHtml?.(selector, insertAfterSelector, html)
  if (!ok) {
    fatalEditorFragmentFallback('insertAfter', insertAfterSelector)
    return false
  }
  return true
}

function replaceOrAppendEditorNode(selector: string, parentSelector: string, html: string): boolean {
  const ok = treeStore.mutate.replaceOrAppendEditorFormNodeHtml?.(selector, parentSelector, html)
  if (!ok) {
    fatalEditorFragmentFallback('append', parentSelector)
    return false
  }
  return true
}

function isVisualOnlyEditorFragmentSelector(selector: string): boolean {
  return selector.includes('mcss-new_to_msg_list-cssm')
    || selector.includes('mcss-avatar_div-cssm')
}

function resolveElement(target: any): HTMLElement | null {
  if (!target) return null
  if (target instanceof HTMLElement) return target
  if (target[0] instanceof HTMLElement) return target[0]
  if (target.el instanceof HTMLElement) return target.el
  if (typeof target.getDOMNode === 'function') {
    const node = target.getDOMNode()
    if (node instanceof HTMLElement) return node
  }
  if (typeof target._eachTarget === 'function') {
    let found: HTMLElement | null = null
    target._eachTarget((el: unknown) => {
      if (!found && el instanceof HTMLElement) found = el
    })
    if (found) return found
  }
  if (typeof target === 'string') {
    const el = runtime.querySelector(target)
    if (el instanceof HTMLElement) return el
  }
  return null
}

function createPlainTextEditor(textarea: HTMLTextAreaElement) {
  const callbacks: Record<string, Array<(...args: any[]) => void>> = {}
  const eventApi = (name: string) => ({
    add(fn: (...args: any[]) => void) {
      callbacks[name] = callbacks[name] || []
      callbacks[name].push(fn)
    },
  })
  const emit = (name: string, ...args: any[]) => {
    for (const fn of callbacks[name] || []) fn(...args)
  }

  const editor = {
    getContent: (_opts?: { format?: string; no_events?: number }) => textarea.value,
    setContent: (value: string) => {
      textarea.value = value == null ? '' : String(value)
      emit('change', editor, value)
    },
    show: () => {
      textarea.style.display = ''
      return editor
    },
    hide: () => {
      // The ONE React textarea serves both rich and plain modes. Legacy hides
      // "the rich editor" when initializing/entering plain mode
      // (new/els.rich _toggle_rich_edit); in the old markup a separate plain
      // input stayed visible — here there is none, so hiding would blank the
      // editor's body field entirely. Mode state still lives in the rough.
      return editor
    },
    focus: () => textarea.focus(),
    selection: {
      select: () => textarea.select(),
      collapse: () => undefined,
      getContent: () => textarea.value.substring(textarea.selectionStart || 0, textarea.selectionEnd || textarea.value.length),
    },
    dom: {
      getRoot: () => textarea,
    },
    onBeforeSetContent: eventApi('beforeSetContent'),
    onPostProcess: eventApi('postProcess'),
    onClick: eventApi('click'),
    onChange: eventApi('change'),
    onKeyPress: eventApi('keyPress'),
  }

  textarea.addEventListener('click', event => emit('click', editor, event))
  textarea.addEventListener('input', event => emit('change', editor, event))
  textarea.addEventListener('keypress', event => emit('keyPress', editor, event))
  ;(textarea as any).__msgPlainRichTextEditor = editor
  return editor
}

function installPlainTextareaRichTextBridge() {
  const plugins = (runtime.legacyRuntime?.plugins || runtime.getLegacyObject('legacyPlugins')) as any
  if (!plugins || plugins.__MSG_PLAIN_TEXTAREA_RICH_TEXT__) return
  const originalRichText = plugins.richText

  plugins.richText = function plainTextareaRichText(target: unknown, opts?: any) {
    const el = resolveElement(target)
    const textarea = el instanceof HTMLTextAreaElement
      ? el
      : el?.querySelector?.('textarea') instanceof HTMLTextAreaElement
        ? el.querySelector('textarea') as HTMLTextAreaElement
        : runtime.querySelector(contractSelector('editor.body.textarea')) as HTMLTextAreaElement | null

    if (!textarea) return typeof originalRichText === 'function' ? originalRichText(target, opts) : undefined

    const editor = (textarea as any).__msgPlainRichTextEditor || createPlainTextEditor(textarea)
    if (opts && typeof opts.setup === 'function' && !(editor as any).__msgSetupCalled) {
      ;(editor as any).__msgSetupCalled = true
      opts.setup(editor)
    }
    if (opts && typeof opts.oninit === 'function') {
      queueMicrotask(() => {
        opts.oninit(editor)
        // oninit is legacy's show_form_2 trigger (the rough value fill /
        // set_form). Republish the now-filled rough to the React editor model.
        dispatchEditorAction({ action: 'sync-model', reason: 'editor-rich-oninit' })
      })
    }
    return editor
  }
  plugins.__MSG_PLAIN_TEXTAREA_RICH_TEXT__ = true
}

function installLegacyNewFormBridge(
  renderEditorTemplate: () => void,
  clearEditorTemplate: () => void,
) {
  installPlainTextareaRichTextBridge()

  w.__MSG_REACT_NEW_FORM__ = {
    renderNewFormFromData() {
      treeStore.mutate.syncLegacyRuntime?.('new-form-local-data')
      renderEditorTemplate()
      const holder = editorHolder()
      w.__MSG_REACT_UI__?.setVisibleElement?.(holder, true)
      installPlainTextareaRichTextBridge()
      installLegacyCheckFormStub()
      return true
    },
    renderNewFormHtml(html: string) {
      void html
      throw new Error('[v2 strict] Legacy new-form HTML parser fallback is disabled; PHP must return modeled msg_inputs/runtime data.')
      renderEditorTemplate()
      const holder = editorHolder()
      w.__MSG_REACT_UI__?.setVisibleElement?.(holder, true)
      installPlainTextareaRichTextBridge()
      rebindLegacyNewForm()
      return true
    },
    clearNewFormHtml() {
      clearEditorTemplate()
      const holder = editorHolder()
      if (holder) w.__MSG_REACT_UI__?.setVisibleElement?.(holder, false)
      return true
    },
  }

  w.__MSG_REACT_NEW_DYNAMIC__ = {
    replaceNewFormNodeHtml(selector: string, html: string) {
      const ok = replaceEditorNode(selector, html)
      if (ok && !isVisualOnlyEditorFragmentSelector(selector)) rebindLegacyNewFormSaverAtts()
      return ok
    },
    replaceOrInsertNewFormNodeHtml(selector: string, insertAfterSelector: string, html: string) {
      const ok = replaceOrInsertEditorNode(selector, insertAfterSelector, html)
      if (ok && !isVisualOnlyEditorFragmentSelector(selector) && !isVisualOnlyEditorFragmentSelector(insertAfterSelector)) rebindLegacyNewFormSaverAtts()
      return ok
    },
    replaceOrAppendNewFormNodeHtml(selector: string, parentSelector: string, html: string) {
      const ok = replaceOrAppendEditorNode(selector, parentSelector, html)
      if (ok && !isVisualOnlyEditorFragmentSelector(selector) && !isVisualOnlyEditorFragmentSelector(parentSelector)) rebindLegacyNewFormSaverAtts()
      return ok
    },
  }
}

function uninstallLegacyNewFormBridge() {
  delete w.__MSG_REACT_NEW_FORM__
  delete w.__MSG_REACT_NEW_DYNAMIC__
}

function syncLegacyAccessPinFromParams(params?: MsgDeskParams | null) {
  const pin = params?.show_pin
  if (!pin || pin === '0') return false

  const msgData = w.msgdsk?.obj?.('data')?.msg_data?.()
  if (!msgData || typeof msgData !== 'object') return false

  if (!msgData.show_pin || msgData.show_pin === '0' || msgData.show_pin === '-1') msgData.show_pin = pin
  if (!msgData.edit_pin || msgData.edit_pin === '0' || msgData.edit_pin === '-1') msgData.edit_pin = pin
  if ('showpin' in msgData) msgData.showpin = pin
  if ('editpin' in msgData) msgData.editpin = pin

  treeStore.mutate.syncLegacyRuntime?.('url-access-pin')
  return true
}

function normalizeLegacyPin(value: unknown): string {
  if (value == null || value === false) return ''
  if (typeof value === 'boolean') return ''
  if (typeof value === 'object') return ''
  const stringValue = String(value).trim()
  if (!stringValue || stringValue === '0' || stringValue === '-1' || stringValue === 'false') return ''
  return stringValue
}

function resolveExplicitAccessPin(params: MsgDeskParams | null | undefined, parentData: Record<string, unknown> | null, editPin?: string) {
  return normalizeLegacyPin(editPin)
    || normalizeLegacyPin(params?.show_pin)
    || normalizeLegacyPin(parentData?.edit_pin)
    || normalizeLegacyPin(parentData?.show_pin)
    || normalizeLegacyPin(parentData?.editpin)
    || normalizeLegacyPin(parentData?.showpin)
}

function parseLegacyJsonObject(result: unknown) {
  if (result && typeof result === 'object') return result as Record<string, any>
  if (typeof result !== 'string') return null
  try {
    return JSON.parse(result) as Record<string, any>
  } catch {
    try {
      return w.$?.parseJSON?.(result) || null
    } catch {
      return null
    }
  }
}

function installLegacyExplicitNewFormBridge(
  params: MsgDeskParams | null | undefined,
  syncEditorIdentity: (identity: EditorSessionIdentity) => void,
) {
  const msgObj = w.MSG_OBJ
  if (!msgObj || typeof msgObj.mev_msg_show_new_msg_evm !== 'function' || msgObj.__MSG_V2_EXPLICIT_NEW_FORM__) {
    return false
  }

  msgObj.mev_msg_show_new_msg_evm = function explicitShowNewMsg(
    isUpdate: boolean,
    isMsg: boolean,
    elId?: number,
    editPin?: string,
  ) {
    const utils = w.MSG_UTILS || w.msgdsk?.obj?.('utils') || w.dsk?.obj?.('utils')
    const dataObj = w.msgdsk?.obj?.('data') || w.dsk?.obj?.('data')
    const elsObj = w.msgdsk?.obj?.('els') || w.dsk?.obj?.('els')
    const alertObj = w.msgdsk?.obj?.('alert') || w.dsk?.obj?.('alert')
    const modeFn = w.msgdsk?.obj?.('modes')?.dsk_mode || w.dsk?.obj?.('modes')?.dsk_mode
    const newObj = w.msgdsk?.mod?.('new_obj')

    if (
      !utils || typeof utils.ajax_get !== 'function'
      || !dataObj || typeof dataObj.msg_data !== 'function'
      || !alertObj || typeof alertObj.alert !== 'function'
      || !elsObj || typeof elsObj.show_mode !== 'function'
      || typeof modeFn !== 'function'
      || !newObj || typeof newObj.show_form !== 'function' || typeof newObj.save_form !== 'function'
    ) {
      throw new Error('[v2 strict] Missing explicit new_form dependencies')
    }

    const newMode = utils.is_undefined?.(isUpdate) || utils.is_undefined?.(isMsg)
      ? 'new_msg'
      : (isUpdate ? 'update_msg' : (isMsg ? 'new_submsg' : 'new_answer'))
    const parentData = dataObj.msg_data() as Record<string, unknown> | null
    const editedData = isUpdate
      ? dataObj.msg_data(elId || 0) as Record<string, unknown> | null
      : null
    // The legacy update command commonly passes `isMsg=true` even when the
    // current main object is an answer; legacy itself resolves the real kind
    // from msg_data. React must preserve that same target identity.
    const identityIsMsg = newMode === 'new_answer'
      ? false
      : newMode === 'update_msg'
        ? Boolean(editedData?.is_msg ?? parentData?.is_msg ?? isMsg)
        : true
    const setModeAndIdentity = () => {
      modeFn(newMode)
      // Legacy receives the entity kind explicitly. Keep it beside the mode
      // in React instead of trying to reconstruct it later from `update_msg`.
      syncEditorIdentity({ mode: newMode, isMsg: identityIsMsg })
    }
    const isFirstly = !msgObj.__MSG_V2_NEW_FORM_READY__
    const isEditPin = Boolean(isUpdate || isMsg)
    const pin = resolveExplicitAccessPin(params, parentData, editPin)
    const isEditLocked = isEditPin && typeof dataObj.is_not_locked === 'function'
      ? !dataObj.is_not_locked(0, true)
      : false
    const isForbiddenByParent = isForbiddenByParentValues(dataObj, isUpdate, isMsg)
    const elsNames = cachedLegacyElsNames()
    const requestPin = pin || false
    const ajaxObj: Record<string, unknown> = {
      is_update: isUpdate ? 1 : 0,
      is_msg: isMsg ? 1 : 0,
      is_firstly: isFirstly ? 1 : 0,
      pin: requestPin,
      is_edit_pin: isEditPin ? 1 : 0,
      upload_settings: 1,
      parent_is_msg: parentData?.is_msg ? 1 : 0,
      parent_code_key: parentData?.code_key,
      parent_msg_id: parentData?.msg_id,
    }

    if (!isFirstly && (!isEditPin || pin)) {
      setModeAndIdentity()
      treeStore.mutate.syncLegacyRuntime?.(`explicit-new-form-reopen:${newMode}`)

      if (!isReactEditorRendered()) {
        if (!w.__MSG_REACT_NEW_FORM__?.renderNewFormFromData?.()) {
          throw new Error('[v2 strict] Missing React reopen new-form renderer')
        }
        msgObj.__MSG_V2_NEW_FORM_READY__ = true
      }

      if (newObj.mod?.().rough_obj?.().is_last?.(elId || 0, isUpdate, isMsg)) return elsObj.show_mode()

      whenEditorTemplateRendered(() => {
        newObj.save_form()
        newObj.show_form(elId, isUpdate, isMsg)
        syncLegacyRuntimeAfterEditorInit(`explicit-new-form-reopen-shown:${newMode}`)
      })
      return
    }

    if (isForbiddenByParent) return

    if (canOpenEditorLocally(dataObj, isUpdate, isMsg, isEditPin, isEditLocked, elId)) {
      setModeAndIdentity()

      if (!w.__MSG_REACT_NEW_FORM__?.renderNewFormFromData?.()) {
        throw new Error('[v2 strict] Missing React local new-form renderer')
      }

      msgObj.__MSG_V2_NEW_FORM_READY__ = true
      whenEditorTemplateRendered(() => {
        newObj.show_form(elId, isUpdate, isMsg, elsNames || undefined)
        syncLegacyRuntimeAfterEditorInit('explicit-new-form-local-open')
      })

      void utils.ajax_get('new_form', ajaxObj, (result: unknown) => {
        if (!result) return
        const parsed = parseLegacyJsonObject(result)
        if (!parsed) return
        applyNewFormResponseMetadata(parsed, {
          pin,
          requestPin,
          parentData,
          dataObj,
          elsObj,
          isEditPin,
          isEditLocked,
          elId,
        })
        treeStore.mutate.syncLegacyRuntime?.('explicit-new-form-background-refresh')
      })

      alertObj.alert()
      return
    }

    if (!editPin && isEditPin && !pin) {
      return alertObj.alert('locked_msg', false, (editPinVal?: string) => {
        if (!editPinVal) return
        return msgObj.mev_msg_show_new_msg_evm(isUpdate, isMsg, elId, editPinVal)
      })
    }

    let paintedFromRuntimeData = false
    if (isEditPin && !isEditLocked && hasMsgInputs(dataObj, elId)) {
      setModeAndIdentity()
      if (!w.__MSG_REACT_NEW_FORM__?.renderNewFormFromData?.()) {
        throw new Error('[v2 strict] Missing React optimistic new-form renderer')
      }
      msgObj.__MSG_V2_NEW_FORM_READY__ = true
      paintedFromRuntimeData = true
    }

    if (!editPin && !paintedFromRuntimeData) alertObj.alert('please_wait')
    if (w.IS_TEST_MODE) ajaxObj.test = 1

    return utils.ajax_get('new_form', ajaxObj, (result: unknown) => {
      if (!result) {
        return alertObj.alert('not_ok')
      }

      if (isEditPin && !paintedFromRuntimeData) alertObj.alert('close')
      if (!paintedFromRuntimeData) setModeAndIdentity()

      const parsed = parseLegacyJsonObject(result)
      if (!parsed) {
        return alertObj.alert('not_ok')
      }

      const responsePin = normalizeLegacyPin(parsed.show_pin) || pin
      parsed.show_pin = responsePin || false

      applyNewFormResponseMetadata(parsed, {
        pin,
        requestPin,
        parentData,
        dataObj,
        elsObj,
        isEditPin,
        isEditLocked,
        elId,
      })

      const nextElsNames = cacheLegacyElsNames(parsed.els_names) || cachedLegacyElsNames()

      if (!paintedFromRuntimeData) {
        const canRenderFromRuntimeData = hasMsgInputs(dataObj, elId)
        const rendered = canRenderFromRuntimeData
          ? w.__MSG_REACT_NEW_FORM__?.renderNewFormFromData?.()
          : w.__MSG_REACT_NEW_FORM__?.renderNewFormHtml?.(parsed.html)

      if (!rendered) {
          throw new Error('[v2 strict] Missing React new-form renderer')
        }
      }

      msgObj.__MSG_V2_NEW_FORM_READY__ = true
      whenEditorTemplateRendered(() => {
        newObj.show_form(elId, isUpdate, isMsg, nextElsNames || undefined)
        syncLegacyRuntimeAfterEditorInit('explicit-new-form-response')
        alertObj.alert()
      })
      return
    })
  }
  msgObj.__MSG_V2_EXPLICIT_NEW_FORM__ = true
  return true
}

function extractLegacyPayload(html: string, idPattern: RegExp): string | null {
  const match = html.match(idPattern)
  const raw = match?.[2]?.trim()
  if (!raw) return null
  // Entities are decoded after the parse (payloadJson.ts) — legacy re-parses
  // this exact string, so it has to stay valid JSON.
  const text = decodePayloadJson(raw).trim()
  return text || null
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function legacyPayloadIdPattern(...contracts: LegacyContractName[]): RegExp {
  const ids = contracts.map(contract => legacyId(contract)).filter(Boolean).map(id => escapeRegExp(id as string))
  return new RegExp(`<(div|span)[^>]*id=['"](?:${ids.join('|')})['"][^>]*>([\\s\\S]*?)<\\/\\1>`, 'i')
}

function cacheLegacyPayloads(html: string) {
  const setPayload = w.all_els_obj?.set_data_payload
  if (!setPayload) return
  const msges = extractLegacyPayload(html, legacyPayloadIdPattern('legacyData.msges', 'legacyData.msgesJson'))
  const init = extractLegacyPayload(html, legacyPayloadIdPattern('legacyData.init'))
  if (msges) setPayload('msges', msges)
  if (init) setPayload('init', init)
}

function importAuxiliaryCollectionSpan(span: BootCollectionSpan): void {
  const reason = `initial-auxiliary-${span.mode}-${span.section}`
  if (!treeStore.mutate.mirrorLegacySubsResponse(span.html, reason)) {
    throw new Error(`[v2 strict] Missing ${span.mode} ${span.section} auxiliary collection payload`)
  }
  runtime.facade.importAuxiliaryCollectionSpan(span.mode, span.section, span.html)
}

function phpModelItemElId(item: any): number {
  const candidates = [item?.ttujtu, item?.last_elid, item?.raw_id, item?.id]
  for (const candidate of candidates) {
    const id = Number(candidate)
    if (Number.isInteger(id) && id > 0) return id
  }
  return 0
}

function phpModelSectionItems(section: any): any[] {
  return Array.isArray(section?.items) ? section.items : []
}

function phpModelTreeItems(section: any): unknown[] {
  return phpModelSectionItems(section)
    .map(item => {
      const id = phpModelItemElId(item)
      const counts = Array.isArray(item?.count_subs)
        ? item.count_subs[0]
        : (item?.count_subs_obj?.subs || [Number(item?.count_answers || 0), Number(item?.count_submsges || 0)])
      return { id, item, counts }
    })
    .filter((entry, index, entries) => entry.id > 0 && entries.findIndex(other => other.id === entry.id) === index)
    .map(entry => [[
      entry.id,
      [[], [], [], [], [], []],
      [0, 0],
      [],
      [],
      entry.counts,
    ], [], []])
}

function legacySeedSubData(data: any): unknown {
  if (!data || typeof data !== 'object') return data
  const { subs_tree: _subsTree, subs_tree_drafts: _draftsTree, subs_tree_trash: _trashTree, subs_tree_favs: _favsTree, ...rest } = data
  if (!Array.isArray(rest.count_subs) && rest.count_subs_obj && typeof rest.count_subs_obj === 'object') {
    rest.count_subs = [
      rest.count_subs_obj.subs || [Number(rest.count_answers || 0), Number(rest.count_submsges || 0)],
      rest.count_subs_obj.subs_loaded || [Number(rest.count_answers_loaded || 0), Number(rest.count_submsges_loaded || 0)],
      rest.count_subs_obj.drafts || [0, 0],
      rest.count_subs_obj.trash || [0, 0],
      rest.count_subs_obj.favs || [0, 0],
      rest.count_subs_obj.faveds || [0, 0],
      rest.count_subs_obj.parents || [0, 0],
      rest.count_subs_obj.atts || 0,
    ]
  }
  return rest
}

function phpModelSubsById(payload: MsgDeskPhpModelResponse): Record<string, unknown> {
  const subs: Record<string, unknown> = {}
  const main = payload.model?.main && payload.model?.body?.html !== undefined
    ? { ...payload.model.main, html: payload.model.body.html, body_html: payload.model.body.html }
    : payload.model?.main
  if (main) subs[0] = legacySeedSubData(main)
  for (const section of [payload.model?.subs?.answers, payload.model?.subs?.submsges]) {
    for (const item of phpModelSectionItems(section)) {
      const elId = phpModelItemElId(item)
      if (elId > 0) subs[elId] = legacySeedSubData(item)
    }
  }
  return subs
}

function cacheLegacyPayloadsFromModel(payload: MsgDeskPhpModelResponse) {
  const setPayload = w.all_els_obj?.set_data_payload
  if (!setPayload || !payload.model?.main) return
  const main = payload.model.main as any
  const answers = payload.model.subs?.answers
  const submsges = payload.model.subs?.submsges
  const allIds = [
    100,
    ...phpModelSectionItems(answers).map(phpModelItemElId),
    ...phpModelSectionItems(submsges).map(phpModelItemElId),
  ].filter(id => id > 0)
  const labels = Array.isArray((main as any).els_labels) ? (main as any).els_labels : Array(16).fill('')
  labels[6] = labels[6] || 'msgdesk.com'

  setPayload('init', JSON.stringify({
    SERVERTIME: Math.floor(Date.now() / 1000),
    els_labels: labels,
    LASTELEMENTID: Math.max(...allIds),
    msg_modes: (main as any).msg_modes || undefined,
    is_locked: !!main.showpin,
    title: main.name_key || main.namekey_subject || main.subject || main.code_key || '',
    is_left_menu: !!main.is_values?.is_left_menu,
  }))

  setPayload('msges', JSON.stringify({
    last_el_id: Math.max(...allIds),
    subs: phpModelSubsById(payload),
    subs_tree: [
      phpModelTreeItems(answers),
      phpModelTreeItems(submsges),
    ],
    msg_modes: (main as any).msg_modes || undefined,
  }))
}

function getLegacyPagingSectionId(el: Element) {
  const section = el.closest([
    contractSelector('subs.answers.front'),
    contractSelector('subs.submsges.front'),
    contractSelector('subs.drafts.answersFront'),
    contractSelector('subs.drafts.submsgesFront'),
    contractSelector('subs.trash.answersFront'),
    contractSelector('subs.trash.submsgesFront'),
    contractSelector('subs.favs.answersFront'),
    contractSelector('subs.favs.submsgesFront'),
  ].join(','))
  return section?.getAttribute('data-md-contract') || section?.id || ''
}

function sectionIdIsSubmsges(sectionId: string) {
  return sectionId.includes('submsges') || /_1-cssm$/.test(sectionId)
}

function resolveLegacyPrevNextPagingClick(target: EventTarget | null) {
  if (!(target instanceof Element)) return null
  const link = target.closest(`a.${legacyCssClass('subs.paging.page')}, a.md-paging__page`)
  if (!(link instanceof HTMLElement)) return null
  const span = link.parentElement
  const bar = link.closest(`.${legacyCssClass('subs.paging.root')}, .md-paging`)
  if (!span || span.tagName.toLowerCase() !== 'span' || !span.classList.contains('md-paging__sequence')) return null
  if (!bar || span.parentElement !== bar) return null
  const links = Array.from(span.querySelectorAll(`:scope > a.${legacyCssClass('subs.paging.page')}, :scope > a.md-paging__page`))
  const index = links.indexOf(link)
  if (index !== 0 && index !== 1) return null
  const sectionId = getLegacyPagingSectionId(link)
  const isSubmsges = sectionIdIsSubmsges(sectionId)
  return {
    el: link,
    page: index === 0 ? 'prev' : 'next',
    subsTypeId: isSubmsges ? 1 : 0,
    disabled: link.classList.contains(legacyCssClass('subs.paging.disabled')) || link.classList.contains('md-hook-disabled'),
  }
}

function resolveLegacyMoreClick(target: EventTarget | null) {
  if (!(target instanceof Element)) return null
  const link = target.closest(`a.${legacyCssClass('subs.more')}, a.md-more-link`)
  if (!(link instanceof HTMLElement)) return null
  const sectionId = getLegacyPagingSectionId(link)
  return { el: link, subsTypeId: sectionIdIsSubmsges(sectionId) ? 1 : 0 }
}

export interface MsgDeskPageProps {
  isEditorOpen?: boolean
  editorMode?: EditorMode
  editorBridgeReady?: boolean
}

export interface Template2AppProps {
  initialLegacyHtml?: string | null
  /**
   * S1 (docs/27 §4): per-request pre-parsed boot state. When present, the desk
   * view renders immediately (server AND first client render) from this state;
   * the legacy runtime boots in the background and adopts via projection sync.
   */
  initialState?: SubsStoreState | null
  /**
   * S1.5: the desk mode of the SSR route ('trash', 'answers', …). Non-'msg'
   * modes arm the initial-projection hold so the legacy bootstrap's transient
   * main-mode pass does not stomp the server-rendered folder/tab view.
   */
  initialDeskMode?: string
  /**
   * S1 island inversion: the runtime chunk is booted by the client island; this
   * flips true once bootMsgDeskRuntime + installMsgDeskAppBridge completed.
   * The legacy bootstrap effect gates on it.
   */
  runtimeReady?: boolean
  PageComponent: ComponentType<MsgDeskPageProps>
  loadInitialSubs?: boolean
  LoadingComponent: ComponentType
  showPinDialog: ShowPinDialog
  /** Auxiliary collection payloads imported into legacy without changing the
   * visible desk mode (Notes root uses Favorites for Pinned). */
  collectionSpans?: import('@/msgdeskRuntime/state').BootCollectionSpan[]
}

export default function Template2App({
  initialLegacyHtml,
  initialState = null,
  initialDeskMode = 'msg',
  runtimeReady = true,
  PageComponent,
  loadInitialSubs = true,
  LoadingComponent,
  showPinDialog,
  collectionSpans,
}: Template2AppProps) {
  const params: MsgDeskParams | undefined = runtime.params
  const [ready, setReady] = useState(false)
  const [rawHtml, setRawHtml] = useState<string | null>(null)
  const [initialPhpModel, setInitialPhpModel] = useState<MsgDeskPhpModelResponse | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editorSession, setEditorSession] = useState<EditorSessionIdentity>(closedEditorSession)
  const [editorBridgeReady, setEditorBridgeReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bootstrapped = useRef(false)

  // Stage 2a: the legacy bootstrap runs synchronously right after payload
  // ingestion (before the first ready render), so the legacy model exists by
  // the time components mount. Returns false when the legacy runtime is not
  // available; the ready-gated effect below remains as the retry/error path.
  const runLegacyBootstrap = (rawHtmlArg: string | null, phpModelArg: MsgDeskPhpModelResponse | null): boolean => {
    if (bootstrapped.current) return true
    if (!runtime.legacyRuntime || !w.msgdsk || !w.MSG_OBJ) return false
    bootstrapped.current = true
    installLegacyVisualClassFacade()
    if (rawHtmlArg) cacheLegacyPayloads(rawHtmlArg)
    else if (phpModelArg) cacheLegacyPayloadsFromModel(phpModelArg)
    w.msgdsk.init_obj()
    w.MSG_OBJ.mev_msg_init_obj_evm()
    w.MSG_OBJ.mev_msg_init_rendered_html_evm(false)
    for (const span of collectionSpans || []) {
      importAuxiliaryCollectionSpan(span)
    }
    ensureBodyV2Classes()
    syncLegacyAccessPinFromParams(params)
    installLegacyPinAlertFacade(showPinDialog)
    installLegacyBusyNoop()
    installLegacyExplicitNewFormBridge(params, setEditorSession)
    installLegacyCheckFormStub()
    installLegacySubsLoadingBridge()
    installLegacyModeVisibilityBridge((isOpen, mode) => {
      treeStore.mutate.setRenderMode(isOpen ? 'editor' : 'msg')
      setEditorSession(current => {
        if (!isOpen) return closedEditorSession
        return activeEditorSession(current, mode)
      })
      setIsEditorOpen(isOpen)
    })
    setEditorBridgeReady(true)
    if (loadInitialSubs) {
      void Promise.resolve(runtime.facade.loadInitialSubs()).catch(loadError => {
        setError(loadError instanceof Error ? loadError.message : String(loadError))
      })
    }
    if (params?.is_edit_mode) {
      const currentMsgData = w.msgdsk?.obj?.('data')?.msg_data?.()
      w.MSG_OBJ.mev_msg_show_new_msg_evm(true, Boolean(currentMsgData?.is_msg))
    }
    return true
  }

  // S1 island inversion: this effect used to run at mount (the island only
  // rendered the app after the runtime had booted). The desk view is now
  // server-rendered and mounts BEFORE the runtime chunk exists, so the whole
  // ingestion + legacy bootstrap sequence gates on the island's runtimeReady
  // signal. Once the bootstrap completes, the pre-boot action queue replays.
  useEffect(() => {
    if (!runtimeReady || bootstrapped.current) return
    installLegacySelectorAliasesGlobal()
    installLegacyVisualClassFacade()
    runtime.setGlobal('__MSG_V2_STRICT_VIRTUAL_DOM__', true)
    runtime.setGlobal('__MSG_V2_FALLBACKS__', [])
    ensureBodyV2Classes()
    if (!params?.codekey) {
      setError('Missing MsgDesk params codekey')
      return
    }
    // S1.5: arm the adoption hold BEFORE the bootstrap runs its transient
    // main-mode pass. Released by the route-mode projection (or the editor,
    // or any user navigation); the timeout below is the safety net so a
    // failed navigation cannot freeze the chrome.
    if (initialDeskMode !== 'msg' && initialState) {
      treeStore.mutate.setInitialProjectionHold?.(initialDeskMode)
    }
    const completeBootstrap = (ok: boolean) => {
      if (!ok) {
        setError('MsgDesk runtime is not loaded')
        return
      }
      setReady(true)
      markLegacyCommandLive()
      if (treeStore.mutate.getInitialProjectionHold?.()) {
        runtime.setTimeout(() => {
          if (!treeStore.mutate.getInitialProjectionHold?.()) return
          treeStore.mutate.setInitialProjectionHold?.(null)
          forceWorkspaceVisibility(currentLegacyMode())
        }, 8000)
      }
    }
    if (initialLegacyHtml) {
      if (initialLegacyHtml.startsWith(INITIAL_PHP_MODEL_PREFIX)) {
        try {
          const payload = JSON.parse(initialLegacyHtml.slice(INITIAL_PHP_MODEL_PREFIX.length)) as MsgDeskPhpModelResponse
          if (!treeStore.mutate.ingestPhpModel(payload, 'initial-ssr-model')) throw new Error('Invalid initial PHP model')
          setInitialPhpModel(payload)
          completeBootstrap(runLegacyBootstrap(null, payload))
        } catch (modelError) {
          setError(modelError instanceof Error ? modelError.message : String(modelError))
        }
        return
      }
      if (!initialLegacyHtml.startsWith('to_link_')) {
        treeStore.mutate.ingestLegacyDataContainers(initialLegacyHtml, 'initial-ssr-html')
        setRawHtml(initialLegacyHtml)
        completeBootstrap(runLegacyBootstrap(initialLegacyHtml, null))
        return
      }
      runtime.redirectToLegacyLink(initialLegacyHtml)
      return
    }

    fetchMsgHtml(params)
      .then(html => {
        if (!html || html.startsWith('to_link_')) {
          runtime.redirectToLegacyLink(html)
          return
        }
        treeStore.mutate.ingestLegacyDataContainers(html, 'initial-html')
        setRawHtml(html)
        completeBootstrap(runLegacyBootstrap(html, null))
      })
      .catch(htmlErr => setError(String(htmlErr)))
  }, [initialLegacyHtml, runtimeReady])

  useEffect(() => {
    if (!ready) return
    installLegacyNewFormBridge(
      () => {
        const mode = currentLegacyMode()
        setEditorSession(current => activeEditorSession(current, mode))
        setIsEditorOpen(true)
      },
      () => {
        setEditorSession(closedEditorSession)
        setIsEditorOpen(false)
      },
    )
    return uninstallLegacyNewFormBridge
  }, [ready])

  useEffect(() => {
    if (!ready) return
    return installLegacyInlineClickBridge()
  }, [ready])

  useEffect(() => {
    if (!ready) return
    const handleJsonEditorOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ mode?: EditorMode; isMsg?: boolean }>).detail
      const mode = detail?.mode || ''
      if (!mode) return
      treeStore.mutate.setRenderMode('editor')
      setEditorSession(current => activeEditorSession(current, mode, detail?.isMsg))
      setIsEditorOpen(true)
    }
    const handleJsonEditorClose = () => {
      treeStore.mutate.setRenderMode('msg')
      setEditorSession(closedEditorSession)
      setIsEditorOpen(false)
    }
    runtime.addEventListener('msg:v2-editor-open', handleJsonEditorOpen)
    runtime.addEventListener('msg:v2-editor-close', handleJsonEditorClose)
    return () => {
      runtime.removeEventListener('msg:v2-editor-open', handleJsonEditorOpen)
      runtime.removeEventListener('msg:v2-editor-close', handleJsonEditorClose)
    }
  }, [ready])

  useEffect(() => {
    if (!ready) return
    const handleLegacyPagingClick = (event: MouseEvent) => {
      const pagingClick = resolveLegacyPrevNextPagingClick(event.target)
      const moreClick = pagingClick ? null : resolveLegacyMoreClick(event.target)
      if (!pagingClick && !moreClick) return
      event.preventDefault()
      event.stopPropagation()
      if (moreClick) {
        runtime.eventBus.emit('mev_msg_get_more_subs_evm', moreClick.el, 0, moreClick.subsTypeId)
        return
      }
      if (!pagingClick || pagingClick.disabled) return
      runtime.eventBus.emit('mev_msg_subs_set_page_evm', pagingClick.el, pagingClick.subsTypeId, pagingClick.page)
    }
    runtime.addDocumentEventListener('click', handleLegacyPagingClick, true)
    return () => runtime.removeDocumentEventListener('click', handleLegacyPagingClick, true)
  }, [ready])

  if (error) return <div>{error}</div>
  // S1: with a pre-parsed initial state the desk renders immediately — on the
  // server (per-request state through the provider) and on the client (the
  // island seeded the singleton pre-hydration). Without one (php-model boot,
  // legacy no-payload fetch), keep the pre-S1 loading gate.
  if (!ready && !initialState) return <LoadingComponent />
  return (
    <SubsManager initialState={initialState}>
      <DeskDocumentTitle runtimeAdopted={ready} />
      <EditorSessionProvider value={editorSession}>
        <PageComponent isEditorOpen={isEditorOpen} editorMode={editorSession.mode} editorBridgeReady={editorBridgeReady} />
      </EditorSessionProvider>
    </SubsManager>
  )
}
